// Sprint 11b — Project chip for the composer toolbar.
//
// Read-only chip showing which project the current agent belongs to.
// Derives the display name from the agent's projectId; falls back to
// extracting the project slug from agent.workdir (D-006 layout).
//
// DS.6 (sprint aos-ds-composer, §2 D3) — the bespoke `<span>` + Tailwind pill
// is `@ds/data-display/Chip variant="plain" size="xs"`, which is exactly what
// `approved-composer.png` renders: a NON-interactive `<span>` (Chip.tsx:99-108)
// carrying an icon and an ellipsised label. Correct by construction — this chip
// opens nothing. Its Gate AA `arb-px` row drains with the hand-rolled 80 px
// label cap: the piece's own `.label { overflow: hidden; text-overflow:
// ellipsis }` does the truncation, which is what the approved mockup shows —
// and it only fires because the composer's project `ToolbarItem` opts into
// `flexShrink: 1` + `minWidth: 0` (Composer.tsx). Any other host of this chip
// must do the same or the label will not ellipsise.

import Chip from '@ds/data-display/Chip';
import type { Agent } from '../types';
import { Icon } from './atoms/Icon';

function deriveProjectLabel(agent: Agent): string | null {
  if (agent.projectId && agent.projectId !== 'default') {
    return agent.projectId;
  }
  const workdir = agent.workdir || '';
  if (!workdir) return null;
  const match = workdir.match(/\/projects\/([^/]+)/);
  if (match?.[1]) return match[1];
  const parts = workdir.replace(/\/+$/, '').split('/').filter(Boolean);
  return parts[parts.length - 1] ?? null;
}

export function ProjectChip({ agent }: { agent: Agent }) {
  const label = deriveProjectLabel(agent);
  if (!label) return null;

  return (
    <Chip
      variant="plain"
      tone="neutral"
      size="xs"
      label={label}
      icon={<Icon name="Folder" size={12} aria-hidden />}
      title={label}
      data-testid="composer-project-chip"
    />
  );
}
